import { FormEvent, useState } from 'react'
import { Link } from 'react-router-dom'
import { Mail, Loader2, MailCheck } from 'lucide-react'
import { supabase } from '@/lib/supabaseClient'
import { AuthShell } from '@/components/AuthShell'

const isValidEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)

/**
 * "Forgot password" entry point. Sends a Supabase password-recovery email
 * whose link returns the member to /auth/callback with a recovery session;
 * AuthCallbackPage then routes them to /reset-password to pick a new password.
 *
 * We always show the same confirmation regardless of whether the email is on
 * file, so this can't be used to probe which addresses have accounts.
 */
const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    const trimmed = email.trim().toLowerCase()
    if (!isValidEmail(trimmed)) {
      setError('Enter a valid email address.')
      return
    }
    setSubmitting(true)
    try {
      const { error: err } = await supabase.auth.resetPasswordForEmail(trimmed, {
        redirectTo: `${window.location.origin}/auth/callback`,
      })
      if (err) throw err
      setSent(true)
    } catch (err: any) {
      const message: string = err?.message || ''
      if (/rate limit/i.test(message)) {
        setError('Too many requests. Please wait a minute and try again.')
      } else {
        setError(message || 'Could not send the reset email. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (sent) {
    return (
      <AuthShell title="Check your email" subtitle="A password reset link is on its way">
        <div className="text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-white/10 border border-white/20 flex items-center justify-center mb-4">
            <MailCheck size={28} className="text-gold" />
          </div>
          <p className="text-blue-50 text-sm leading-relaxed">
            If an account exists for <span className="font-semibold">{email.trim().toLowerCase()}</span>,
            we've sent a link to reset your password. Check your inbox — and your spam folder, just
            in case.
          </p>
          <p className="text-blue-100/70 text-xs mt-4">
            The link expires after a little while. If it doesn't arrive, you can request another.
          </p>
          <div className="mt-6 flex flex-col gap-3">
            <button
              type="button"
              onClick={() => setSent(false)}
              className="w-full inline-flex items-center justify-center gap-2 py-3 px-6 rounded-full border border-white/40 bg-white/10 text-white font-semibold hover:bg-white/20 transition-colors"
            >
              Use a different email
            </button>
            <Link
              to="/login"
              className="w-full inline-flex items-center justify-center gap-2 py-3 px-6 rounded-full bg-white text-blue-900 font-semibold hover:bg-blue-50 transition shadow-lg"
            >
              Back to sign in
            </Link>
          </div>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell title="Reset your password" subtitle="We'll email you a secure reset link">
      <form onSubmit={handleSubmit} className="space-y-5">
        <label className="block">
          <span className="block text-sm font-medium text-white mb-2">Email</span>
          <div className="relative">
            <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="email"
              autoComplete="email"
              required
              autoFocus
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (error) setError('')
              }}
              placeholder="you@example.com"
              className="w-full pl-10 pr-4 py-3 bg-white/95 border border-gray-200 rounded-lg input-focus text-gray-900 placeholder-gray-400"
            />
          </div>
        </label>

        {error && (
          <div className="bg-red-500/15 border border-red-300/40 text-red-100 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 py-3 px-6 rounded-full bg-white text-blue-900 font-semibold hover:bg-blue-50 transition disabled:opacity-60 shadow-lg"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Sending…
            </>
          ) : (
            'Send reset link'
          )}
        </button>

        <p className="text-center text-xs text-blue-100/70">
          Remembered it? <Link to="/login" className="text-blue-200 hover:text-white underline">Sign in</Link>
        </p>
      </form>
    </AuthShell>
  )
}

export default ForgotPasswordPage
